import React from 'react';
import { Line } from 'react-chartjs-2';
import {
    CHART_TYPE_HOME_PRICE,
    CHART_TYPE_PRICE_VS_INVENTORY,
} from './captions';
import { COLOR_GRID, COLOR_INK, COLOR_TEAL } from './skins';

const FONT_FAMILY = "'LL Circular Pro', Helvetica, Arial, sans-serif";
const AXIS_FONT_SIZE = 14;
const AXIS_TITLE_FONT_SIZE = 16;

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 });

function formatAxisValue(value, chartType) {
    if (!Number.isFinite(value)) return '';
    if (chartType === CHART_TYPE_HOME_PRICE) return `$${compact.format(value)}`;
    return compact.format(value);
}

function buildScale({ id, position, chartType, title }) {
    return {
        id,
        position,
        gridLines: {
            color: COLOR_GRID,
            zeroLineColor: COLOR_GRID,
            drawBorder: false,
            drawTicks: false,
        },
        scaleLabel: title
            ? {
                display: true,
                labelString: title,
                fontFamily: FONT_FAMILY,
                fontSize: AXIS_TITLE_FONT_SIZE,
                fontStyle: 'bold',
                fontColor: COLOR_INK,
                padding: 4,
            }
            : { display: false },
        ticks: {
            fontFamily: FONT_FAMILY,
            fontSize: AXIS_FONT_SIZE,
            fontColor: COLOR_INK,
            padding: 10,
            maxTicksLimit: 5,
            callback: (value)=> formatAxisValue(value, chartType),
        },
    };
}

/**
 * Line chart for the Housing Snapshot shareable. Price vs Inventory plots two
 * datasets against opposite axes; every other market topic plots one.
 *
 * @param {Object} props - Props
 * @param {Array} props.series - `[{ label, primary, secondary }]`
 * @param {string} props.chartType - Chart type
 * @param {Object} [props.axisTitles] - `{ left, right }` for the dual variant
 * @param {string} [props.pointHoleColor] - Fill for the hollow inventory markers
 * @param {Function} [props.onRendered] - Called once the chart has painted
 * @returns {JSX.Element}
 */
export default function Chart({ series, chartType, axisTitles, pointHoleColor, onRendered }) {
    const isDual = chartType === CHART_TYPE_PRICE_VS_INVENTORY;
    const labels = series.map(({ label })=> label);

    const primaryDataset = {
        label: isDual ? (axisTitles && axisTitles.left) || 'Price' : 'Value',
        data: series.map(({ primary })=> primary),
        fill: false,
        spanGaps: true,
        lineTension: 0.45,
        borderWidth: 3,
        borderColor: isDual ? COLOR_INK : COLOR_TEAL,
        backgroundColor: isDual ? COLOR_INK : COLOR_TEAL,
        pointRadius: 5,
        pointBorderWidth: 0,
        pointBackgroundColor: isDual ? COLOR_INK : COLOR_TEAL,
        yAxisID: 'primary',
    };

    const datasets = [primaryDataset];

    if (isDual) {
        datasets.push({
            label: (axisTitles && axisTitles.right) || 'Inventory',
            data: series.map(({ secondary })=> secondary),
            fill: false,
            spanGaps: true,
            lineTension: 0.45,
            borderWidth: 3,
            borderDash: [3, 4],
            borderColor: COLOR_INK,
            backgroundColor: COLOR_INK,
            pointRadius: 5,
            pointBorderWidth: 2,
            pointBorderColor: COLOR_INK,
            pointBackgroundColor: pointHoleColor || '#FFFFFF',
            yAxisID: 'secondary',
        });
    }

    const yAxes = [
        buildScale({
            id: 'primary',
            position: 'left',
            chartType: isDual ? CHART_TYPE_HOME_PRICE : chartType,
            title: axisTitles && axisTitles.left,
        }),
    ];

    if (isDual) {
        yAxes.push(buildScale({
            id: 'secondary',
            position: 'right',
            chartType: 'count',
            title: axisTitles && axisTitles.right,
        }));
    }

    return (
        <Line
            data={{ labels, datasets }}
            options={{
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: false },
                tooltips: { enabled: false },
                hover: { mode: null },
                layout: { padding: { top: 12, right: 4, bottom: 0, left: 0 } },
                scales: {
                    xAxes: [{
                        gridLines: {
                            color: COLOR_GRID,
                            zeroLineColor: COLOR_GRID,
                            drawBorder: false,
                            drawTicks: false,
                        },
                        ticks: {
                            fontFamily: FONT_FAMILY,
                            fontSize: AXIS_FONT_SIZE,
                            fontColor: COLOR_INK,
                            padding: 12,
                            autoSkip: false,
                            maxRotation: 0,
                            minRotation: 0,
                            callback: (value, index)=> (index % 2 === 0 ? value : ''),
                        },
                    }],
                    yAxes,
                },
                animation: {
                    duration: 0,
                    onComplete: ()=> {
                        if (typeof onRendered === 'function') onRendered();
                    },
                },
            }}
        />
    );
}
